import CloseIcon from "@mui/icons-material/CloseRounded";
import SaveIcon from "@mui/icons-material/FileDownloadOutlined";
import type { Theme } from "@mui/material";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography, useMediaQuery } from "@mui/material";
import { useRef } from "react";
import documentEventsApi from "../../api/clients/documentEventsApi";
import { logError } from "../../logging";
import { downloadFileFromUrl } from "../../services/downloadFile";
import VideoPlayer from "./VideoPlayer";

interface Props {
  src: string;
  title: string;
  documentViewSessionId?: string;
  preventDownload?: boolean;
  onClose: () => void;
}

const sendDownloadEvent = async (documentViewSessionId: string) => {
  try {
    await documentEventsApi.ingestDocumentDownloadEvent({
      viewEventsData: [
        {
          sessionId: documentViewSessionId,
          eventData: { download: true },
        },
      ],
    });
  } catch (error) {
    logError(error, "[PreviewVideoDialog] sendDownloadEvent");
  }
};

const PreviewVideoDialog = ({ src, title, documentViewSessionId, preventDownload, onClose }: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));

  const handleDownloadClick = async () => {
    await downloadFileFromUrl(src);

    if (documentViewSessionId) {
      await sendDownloadEvent(documentViewSessionId);
    }
  };

  const handleClose = () => {
    onClose();

    if (videoRef.current) {
      videoRef.current.src = "";
    }
  };

  return (
    <Dialog
      open={Boolean(src)}
      onClose={handleClose}
      maxWidth="lg"
      fullScreen={isSmallScreen}
      slotProps={isSmallScreen ? undefined : { paper: { sx: { minWidth: "20rem", minHeight: "20rem" } } }}
    >
      <DialogTitle sx={{ px: 2, py: 1 }}>
        <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <Typography component="span" variant="h6" noWrap>
            {title}
          </Typography>
          <Stack direction="row" spacing={1}>
            {!preventDownload && (
              <IconButton onClick={handleDownloadClick}>
                <SaveIcon />
              </IconButton>
            )}
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <VideoPlayer
            src={src}
            documentViewSessionId={documentViewSessionId}
            videoRef={videoRef}
            sx={{ flexGrow: 1 }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PreviewVideoDialog;
